import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as farStar } from '@fortawesome/free-regular-svg-icons';
import { faStar } from '@fortawesome/free-solid-svg-icons';

const SeriesCard = ({ series, onSeriesClick, firstAirYear, lastAirYear, numberOfSeasons, ratingFormat = 'stars' }) => {
  if (!series) return null;

  // Create 5‑star rating (used when ratingFormat === 'stars')
  const createStarRating = (rating) => {
    const starRating = rating / 2;
    const fullStars = Math.floor(starRating);
    const partialStar = starRating - fullStars;
    let stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <div className="star" key={`full-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '100%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    if (partialStar > 0) {
      stars.push(
        <div className="star" key="partial">
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: `${partialStar * 100}%` }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    const emptyStars = 5 - Math.ceil(starRating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <div className="star" key={`empty-${i}`}>
          <FontAwesomeIcon icon={farStar} />
          <div className="star-filled" style={{ width: '0%' }}>
            <FontAwesomeIcon icon={faStar} />
          </div>
        </div>
      );
    }
    return stars;
  };

  // Render rating based on format
  const renderRating = () => {
    if (ratingFormat === 'stars') {
      return (
        <div className="star-rating">
          {createStarRating(series.vote_average || 0)}
        </div>
      );
    } else {
      const ratingNumber = series.vote_average ? series.vote_average.toFixed(1) : null;
      return (
        <div className="rating-container">
          <span className="rating-star"><FontAwesomeIcon icon={faStar} /></span>
          <span className="rating-number">{ratingNumber}</span>
        </div>
      );
    }
  };

  const posterPath = series.poster_path
    ? `https://image.tmdb.org/t/p/w500${series.poster_path}`
    : 'https://via.placeholder.com/500x750?text=No+Poster';

  // Build meta string: first year - last year (if different) + seasons
  const firstYear = firstAirYear || (series.first_air_date ? new Date(series.first_air_date).getFullYear() : null);
  const lastYear = lastAirYear || null;
  let yearRange = '';
  if (firstYear && lastYear && firstYear !== lastYear) {
    yearRange = `${firstYear}–${lastYear}`;
  } else if (firstYear) {
    yearRange = `${firstYear}`;
  }
  const seasons = numberOfSeasons || series.number_of_seasons || 0;
  const seasonText = seasons > 0 ? `${seasons} Season${seasons > 1 ? 's' : ''}` : '';

  const metaParts = [];
  if (yearRange) metaParts.push(yearRange);
  if (seasonText) metaParts.push(seasonText);
  const metaText = metaParts.join(' • ');

  return (
    <div
      className="series-card"
      data-id={series.id}
      data-type="tv"
      onClick={() => onSeriesClick(series.id)}
    >
      <div className="poster-container">
        <img src={posterPath} alt={series.name || 'Series'} className="movie-poster" loading="lazy" />
      </div>
      <div className="series-details">
        <div className="movie-title">{series.name?.toUpperCase() || 'UNTITLED'}</div>
        <div className="movie-meta-row">
          <div className="rating-wrapper">
            {renderRating()}
          </div>
          {metaText && (
            <div className="meta-info">{metaText}</div>
          )}
        </div>
      </div>

      <style jsx>{`
        .series-card {
          display: flex;
          flex-direction: column;
          background: #00000000;
          border-radius: 8px;
          overflow: hidden;
          cursor: pointer;
          transition: transform 0.2s;
        }
        .series-card:hover  .movie-poster { transform: scale(1.05)};
         
        }
        .poster-container {
          position: relative;
          overflow: hidden;
          background: #1a1a1a;
        }
        .movie-poster {
          width: 100%;
          display: block;
        }
        .series-details {
          padding: 8px 0px !important;
          text-align: left !important;
        }
        .movie-title {
          font-weight: 600;
          font-size: clamp(12px, 1.1vw, 16px);
          white-space: nowrap;
          overflow: hidden;
          
          color: #fff;
          letter-spacing: 0.3px;
          padding: 0 2px;
        }
        .movie-meta-row {
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          width: 100%;
          gap: 6px;
          padding: 2px 2px 0;
        }
        .rating-wrapper {
          display: flex;
          align-items: center;
          flex-shrink: 0;
          margin: 0;
          padding: 0;
        }
        .star-rating {
          display: flex;
          align-items: center;
          gap: 1px;
          margin: 0;
          padding: 0;
        }
        .star {
          position: relative;
          display: inline-block;
          font-size: clamp(10px, 0.8vw, 14px);
          color: #555;
          line-height: 1;
          margin: 0;
          padding: 0;
        }
        .star .star-filled {
          position: absolute;
          top: 0;
          left: 0;
          overflow: hidden;
          color: #59f001;
        }
        .star .far.fa-star {
          color: #444;
        }
        .rating-container {
          display: flex;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
        }
        .rating-star {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: #59f001;
          line-height: 1;
        }
        .rating-star svg {
          display: inline-block;
        }
        .rating-number {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: #59f001;
          font-weight: 600;
        }
        .meta-info {
          font-size: clamp(10px, 0.77vw, 13.2px);
          color: rgba(255, 255, 255, 0.7);
          white-space: nowrap;
          overflow: hidden;
          
          flex-shrink: 1;
          text-align: right;
        }
        @media (max-width: 768px) {
          .series-details {
            padding: 4px 0px !important;
          }
          .movie-title {
            font-size: 11px;
          }
          .movie-meta-row {
            padding-left: 0 !important;
          }
          .rating-wrapper {
            scale: 1.15 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .star {
            font-size: 16px ;
          }
          .rating-star,
          .rating-number {
            font-size: 13px ;
          }
          .meta-info {
            font-size: 13px;
          }
        }

           @media (max-width: 480px) {
          .movie-title {
            font-size: 10px;
          }
          .star {
            font-size: 10px;
          }
          .rating-star,
          .rating-number {
            font-size: 9px;
                                    padding-top: 1px;

          }
          .meta-info {
            font-size: 10px;
          }
        }
        
        @media (max-width: 430px) {
          .movie-title {
            font-size: 10px;
          }
          .star {
            font-size: 10px ;
          }
          .rating-star,
          .rating-number {
            font-size: 9px ;
                        padding-top: 1px;
          }
          .meta-info {
            font-size: 10px;
          }
        
        }
      `}</style>
    </div>
  );
};

export default SeriesCard;